import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'app-user-avatar',
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule
    ],
    templateUrl: './user-avatar.component.html',
    styleUrls: ['./user-avatar.component.scss'],
    host: { '[class.fill]': 'fill' }
})
export class UserAvatarComponent {
    @Input() avatarUrl?: string;
    @Input() firstName?: string;
    @Input() lastName?: string;
    @Input() fullName?: string;
    @Input() size: number = 30;
    @Input() showStatus: boolean = false;
    @Input() isOnline: boolean = false;
    @Input() loading?: number | null;
    @Input() showUploadProgress: boolean = false;
    @Input() status?: 'on' | 'off';
    @Input() icon?: string;
    @Input() fill: boolean = false;

    private readonly DEFAULT_AVATAR_PATHS = [
        '/img/default-avatar.png',
        '/assets/default-avatar.png',
        'default-avatar.png',
        'user_default.png'
    ];

    private readonly AVATAR_COLORS = [
        '#6264A7', // Teams purple
        '#8764B8', // Violet
        '#C239B3', // Magenta
        '#E3008C', // Pink
        '#DA3B01', // Orange
        '#0078D4', // Blue
        '#00B7C3', // Teal
        '#038387', // Dark teal
        '#8E562E', // Brown
        '#498205', // Green
    ] as const;

    getInitials(): string {
        if (this.fullName?.trim()) {
            return this.extractInitialsFromFullName(this.fullName.trim());
        }

        const first = this.firstName?.trim();
        const last = this.lastName?.trim();

        if (first && first[0] && last && last[0]) {
            return (first[0] + last[0]).toUpperCase();
        }

        if (first && first[0]) {
            return first.length >= 2
                ? first.substring(0, 2).toUpperCase()
                : first[0].toUpperCase();
        }

        return '?';
    }

    private extractInitialsFromFullName(fullName: string): string {
        const parts = fullName.split(/\s+/).filter(p => p.length > 0);

        if (parts.length >= 2) {
            const firstPart = parts[0];
            const lastPart = parts[parts.length - 1];

            if (firstPart && firstPart[0] && lastPart && lastPart[0]) {
                return (firstPart[0] + lastPart[0]).toUpperCase();
            } else {
                return '?';
            }
        }

        if (parts.length === 1 && parts[0]) {
            const part = parts[0];

            if (!part[0]) {
                return '?';
            } else {
                return part.length >= 2 ? part.substring(0, 2).toUpperCase() : part[0].toUpperCase();
            }
        }

        return '?';
    }

    hasCustomAvatar(): boolean {
        if (!this.avatarUrl?.trim()) {
            return false;
        }

        const url = this.avatarUrl.trim().toLowerCase();

        return !this.DEFAULT_AVATAR_PATHS.some(path =>
            url.includes(path.toLowerCase())
        );
    }

    getBackgroundColor(): string {
        const name = this.getNameForColor();
        const hash = this.generateHashFromString(name);
        const colorIndex = Math.abs(hash) % this.AVATAR_COLORS.length;

        return this.AVATAR_COLORS[colorIndex] ?? this.AVATAR_COLORS[0];
    }

    private getNameForColor(): string {
        if (this.fullName?.trim()) {
            return this.fullName.trim();
        }

        const firstName = this.firstName?.trim() || '';
        const lastName = this.lastName?.trim() || '';

        return `${firstName} ${lastName}`.trim() || 'default';
    }

    private generateHashFromString(str: string): number {
        return str.split('').reduce((hash, char) => {
            return char.charCodeAt(0) + ((hash << 5) - hash);
        }, 0);
    }

    getAvatarSize(): string {
        return this.fill ? '100%' : `${this.size}px`;
    }

    getFontSize(): string {
        return this.fill ? '' : `${this.size * 0.4}px`;
    }

    isLoading(): boolean {
        return this.showUploadProgress && this.loading !== null && this.loading !== undefined;
    }
}